import { ExpectedDeliveryDateView, PresentedDeliveryMethod } from '@farmasiet/frontendcore-reactcomponents';
import React, { useContext } from 'react';

import { Loader } from '../../Components/Loader/Loader';
import { ExpectedDeliveryDateViewModel } from '../../Purchase/Checkout/Delivery/DeliveryMethods/Common/ExpectedDelivery/ExpectedDeliveryDateViewModel.csharp';
import { getFor, post } from '../API';
import { UserContext, UserContextProvider } from '../Contexts/UserContext';
import { expectedDeliveryDate } from './ExpectedDeliveryDate';
import { ExpectedDeliveryDateModal } from './ExpectedDeliveryDateModal/ExpectedDeliveryDateModal';
import { ExpectedDeliveryDateProps } from './ExpectedDeliveryDateProps.csharp';

export const ExpectedDeliveryDateWrapper = (props: ExpectedDeliveryDateProps) => {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [isInitiated, setIsInitiated] = React.useState(false);

    const userContext = useContext(UserContext);

    const { selectedDeliveryMethod, fastestDeliveryMethod, deliveryMethods } = expectedDeliveryDate;
    const postalCode = expectedDeliveryDate.postalCode || userContext.user?.address?.postalCode || null;
    const isAnyDeliveryMethodAvailable = expectedDeliveryDate.availableDeliveryMethods.length > 0;
    const presentedDeliveryMethod =
        selectedDeliveryMethod && expectedDeliveryDate.isDeliveryMethodAvailable(selectedDeliveryMethod)
            ? selectedDeliveryMethod
            : fastestDeliveryMethod && expectedDeliveryDate.isDeliveryMethodAvailable(fastestDeliveryMethod)
            ? fastestDeliveryMethod
            : isAnyDeliveryMethodAvailable
            ? expectedDeliveryDate.availableDeliveryMethods[0]
            : selectedDeliveryMethod || fastestDeliveryMethod;
    const isPresentedDeliveryMethodAvailable = presentedDeliveryMethod && expectedDeliveryDate.isDeliveryMethodAvailable(presentedDeliveryMethod);

    React.useEffect(() => {
        expectedDeliveryDate.setMethodsAvailableForProduct(props.availableMethods);
    }, []);

    React.useEffect(() => {
        if (!userContext.isReady) {
            return;
        }

        if (postalCode && (!deliveryMethods?.methods?.length || deliveryMethods?.postalCode !== postalCode)) {
            refreshCache();
        } else {
            setIsInitiated(true);
        }
    }, [userContext.isReady]);

    const refreshCache = async () => {
        try {
            const results = await getDeliveryMethods(postalCode!);
            expectedDeliveryDate.setDeliveryMethods(results, postalCode);
            expectedDeliveryDate.setPostalCode(postalCode!);

            if (selectedDeliveryMethod) {
                const updatedSelectedMethod = results.find((x) => x.methodName === selectedDeliveryMethod.methodName);
                updatedSelectedMethod && expectedDeliveryDate.selectDeliveryMethod(updatedSelectedMethod);
            }
        } finally {
            setIsInitiated(true);
        }
    };

    const getDeliveryMethods = async (_postalCode: string) => {
        return await getFor<ExpectedDeliveryDateViewModel[]>(`${props.getExpectedDeliveryDateUrl}?postalCode=${_postalCode}`);
    };

    if (!isInitiated) {
        return (
            <div className="ExpectedDeliveryDateView">
                <Loader isLoading />
            </div>
        );
    }

    const logCustomEvent = (name: string, properties?: { [key: string]: string }) => {
        post(props.logCustomEventUrl, { name, properties }).catch(() => ({}));
    };

    const openModal = () => {
        logCustomEvent('Displayed expected delivery date modal');
        setIsModalOpen(true);
    };

    const closeModal = () => setIsModalOpen(false);

    const buildPresentDeliveryMethodModel = (): PresentedDeliveryMethod => {
        return {
            methodName: presentedDeliveryMethod?.methodName,
            imgUrl: presentedDeliveryMethod?.logo?.url,
            expectedDeliveryDate: presentedDeliveryMethod?.expectedDate
        } as PresentedDeliveryMethod;
    };

    return (
        <UserContextProvider>
            <ExpectedDeliveryDateModal
                deliveryInfoUrl={props.deliveryInfoPage}
                isOpen={isModalOpen}
                onClose={closeModal}
                getDeliveryMethods={getDeliveryMethods}
                initialPostalCode={postalCode}
                logoPictureProfile={props.logoPictureProfile}
                logCustomEvent={logCustomEvent}
            />
            <ExpectedDeliveryDateView
                postalCode={postalCode}
                presentedDeliveryMethod={buildPresentDeliveryMethodModel()}
                openModal={openModal}
                isPresentedDeliveryMethodAvailable={isPresentedDeliveryMethodAvailable}
                isAnyDeliveryMethodAvailable={isAnyDeliveryMethodAvailable}
                isCooledProduct={props.isCooledProduct}
                cooledDeliveryInfoPage={props.cooledDeliveryInfoPageUrl}
            />
        </UserContextProvider>
    );
};
